import React, { useState, useRef, useEffect } from 'react';
import { X, Camera, Upload, Paperclip, Loader2, Share2, Pencil } from 'lucide-react';
import { useApp } from '@/context/AppContext';
import { Event, EventType, FileCategory, PendingAttachment, ProjectFile,UserTag } from '@/types';
import { cn,generateVideoThumbnail } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { toast } from '@/hooks/use-toast';
import { EventService, UpdateEventPayload } from '@/services/EventService';
import { fileService } from '@/services/fileService';
import { userService } from '@/services/usersService';
import { Capacitor } from '@capacitor/core';
import CameraPreviewModal from '@/components/CameraPreviewModal';
import AttachmentPreviewModal from '@/components/AttachmentPreviewModal';
import ImageEditor from '@/components/ImageEditor';
import PendingAttachmentTile from '@/components/new-document/PendingAttachmentTile';
import ExistingAttachmentTile from '@/components/new-document/ExistingAttachmentTile';
import TagSelector from '@/components/new-document/TagSelector';
import { shareAttachments, shareAttachmentsWeb, ShareItem } from '@/lib/shareAttachments';
import RenameFileDialog from '@/components/RenameFileDialog';


interface NewDocumentModalProps {
  open: boolean;
  onClose: (success: boolean) => void;
  preselectedProjectId?: string;
  editEvent?: Event | null;
  reloadEvents?: () => void;
}

const eventTypes: { value: EventType; label: string; color: string }[] = [
  { value: 'visit', label: 'ביקור באתר', color: 'bg-primary' },
  { value: 'progress', label: 'שאלה', color: 'bg-success' },
  { value: 'fault', label: 'תקלה', color: 'bg-destructive' },
];


export default function NewDocumentModal({ open, onClose, preselectedProjectId, editEvent, reloadEvents }: NewDocumentModalProps) {
  const { projects, getEventFiles, userDetails, setUserDetails } = useApp();
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isEditMode = !!editEvent;

  const [step, setStep] = useState(preselectedProjectId || isEditMode ? 2 : 1);
  const [selectedProject, setSelectedProject] = useState(editEvent?.projectId || preselectedProjectId || '');
  const [eventType, setEventType] = useState<EventType>(editEvent?.type || 'visit');
  const [eventDate, setEventDate] = useState(
    editEvent?.date
      ? new Date(editEvent.date).toISOString().split('T')[0]
      : new Date().toISOString().split('T')[0]
  );
  const [description, setDescription] = useState(editEvent?.text || '');
  const [selectedTags, setSelectedTags] = useState<string[]>((editEvent?.tags || []).map(t => t.id));
  // Audit metadata for the current edit save only — never preloaded, edit mode only.
  const [changeReason, setChangeReason] = useState('');
  const [pendingAttachments, setPendingAttachments] = useState<PendingAttachment[]>([]);
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [existingAttachments, setExistingAttachments] = useState<ProjectFile[]>([]);
  const [removedAttachmentIds, setRemovedAttachmentIds] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [showCameraPreview, setShowCameraPreview] = useState(false);
  const [availableTags, setAvailableTags] = useState<UserTag[]>([]);
  const [newTagName, setNewTagName] = useState('');
  const [showNewTagInput, setShowNewTagInput] = useState(false);
  const [isCreatingTag, setIsCreatingTag] = useState(false);

  const [draftEventId, setDraftEventId] = useState<string | null>(null);
  const draftEventIdRef = useRef<string | null>(null);          // synchronous mirror for rapid handlers
  const draftPromiseRef = useRef<Promise<string | null> | null>(null); // in-flight POST /events/draft — dedup guard
  const lastSavedRef = useRef<{ text?: string; date?: string; type?: EventType; tagIds?: string[] }>({}); // last PATCHed values (skip no-op saves)
  const uploadPromisesRef = useRef<Map<string, Promise<void>>>(new Map()); // in-flight uploads per attachment uid (no double upload)

  const isAndroid = Capacitor.getPlatform() === 'android';
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [isSharing, setIsSharing] = useState(false);
  const [renameTarget, setRenameTarget] = useState<string | null>(null); // selected key being renamed
  const [isRenaming, setIsRenaming] = useState(false);
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressFiredRef = useRef(false);

  const pendingKey = (index: number) => `p:${index}`;
  const existingKey = (fileId: string) => `e:${fileId}`;

  useEffect(() => () => {
    if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
  }, []);

  const exitSelection = () => {
    setSelectionMode(false);
    setSelectedKeys(new Set());
  };

  const toggleSelection = (key: string) => {
    setSelectedKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      if (next.size === 0) {
        setSelectionMode(false);
      }
      return next;
    });
  };

  const clearLongPressTimer = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const tileHandlers = (key: string, onTap?: () => void) => {
    if (!isAndroid) {
      return { onClick: onTap };
    }
    return {
      onTouchStart: () => {
        longPressFiredRef.current = false;
        clearLongPressTimer();
        longPressTimerRef.current = setTimeout(() => {
          longPressFiredRef.current = true;
          setSelectionMode(true);
          setSelectedKeys(prev => new Set(prev).add(key));
        }, 500);
      },
      onTouchEnd: clearLongPressTimer,
      onTouchMove: clearLongPressTimer,
      onTouchCancel: clearLongPressTimer,
      onContextMenu: (e: React.MouseEvent) => e.preventDefault(),
      onClick: () => {
        if (longPressFiredRef.current) {
          // Consume the click that follows a long-press so it doesn't open the preview.
          longPressFiredRef.current = false;
          return;
        }
        if (selectionMode) {
          toggleSelection(key);
          return;
        }
        onTap?.();
      },
    };
  };

  const handleShare = async () => {
    if (selectedKeys.size === 0) return;
    setIsSharing(true);
    try {
      const items: ShareItem[] = [];
      selectedKeys.forEach(key => {
        if (key.startsWith('p:')) {
          const index = Number(key.slice(2));
          const att = pendingAttachments[index];
          if (!att) return;
          const isImage = att.file.type.startsWith('image/');
          if (isImage && att.base64 && att.base64 !== '#mock-url') {
            items.push({ kind: 'dataUrl', fileName: att.file.name, mimeType: att.file.type, dataUrl: att.base64 });
          } else {
            items.push({ kind: 'file', fileName: att.file.name, file: att.file });
          }
        } else if (key.startsWith('e:')) {
          const fileId = key.slice(2);
          const file = existingAttachments.find(f => f.id === fileId);
          const url = file?.url || file?.thumbnailUrl;
          if (file && url) {
            items.push({ kind: 'remote', fileName: file.name, url });
          }
        }
      });

      if (items.length === 0) return;
      // Native (Android) uses the Capacitor share path; web/desktop uses the Web Share API.
      if (Capacitor.isNativePlatform()) {
        await shareAttachments(items);
      } else {
        await shareAttachmentsWeb(items);
      }
      exitSelection();
    } catch (error) {
      toast({
        title: 'שגיאה בשיתוף',
        description: 'אירעה שגיאה בשיתוף הקבצים',
        variant: 'destructive',
      });
    } finally {
      setIsSharing(false);
    }
  };

  // Resolves the current (full) file name for a selection key (`p:` pending / `e:` existing).
  const getSelectedFileName = (key: string | null): string => {
    if (!key) return '';
    if (key.startsWith('p:')) {
      return pendingAttachments[Number(key.slice(2))]?.file.name || '';
    }
    if (key.startsWith('e:')) {
      return existingAttachments.find(f => f.id === key.slice(2))?.name || '';
    }
    return '';
  };

  const handleRename = async (newName: string) => {
    const key = renameTarget;
    if (!key || isRenaming) return;
    setIsRenaming(true);
    try {
      if (key.startsWith('p:')) {
        const index = Number(key.slice(2));
        const att = pendingAttachments[index];
        if (!att) return;
        // Rebuild the File so the renamed object is what eventually gets uploaded.
        const renamedFile = new File([att.file], newName, { type: att.file.type });
        setPendingAttachments(prev =>
          prev.map((a, i) => (i === index ? { ...a, file: renamedFile } : a))
        );
        // If this pending attachment already has a backend copy (draft), keep it in sync.
        if (att.uploadedFileId) {
          await fileService.renameFile(att.uploadedFileId, newName);
        }
      } else if (key.startsWith('e:')) {
        const fileId = key.slice(2);
        const prevName = existingAttachments.find(f => f.id === fileId)?.name;
        // Optimistic UI update.
        setExistingAttachments(prev =>
          prev.map(f => (f.id === fileId ? { ...f, name: newName } : f))
        );
        try {
          const updated = await fileService.renameFile(fileId, newName);
          // Reconcile with the server response (use its name if returned).
          if (updated && typeof updated.name === 'string') {
            setExistingAttachments(prev =>
              prev.map(f => (f.id === fileId ? { ...f, name: updated.name } : f))
            );
          }
        } catch (err) {
          // Revert optimistic change on failure.
          setExistingAttachments(prev =>
            prev.map(f => (f.id === fileId ? { ...f, name: prevName ?? f.name } : f))
          );
          throw err;
        }
      }
      toast({ title: 'שם הקובץ עודכן', style: { direction: 'rtl' } });
      setRenameTarget(null);
      exitSelection();
    } catch (error) {
      toast({
        title: 'שגיאה בשינוי שם הקובץ',
        description: 'אירעה שגיאה בעדכון שם הקובץ',
        variant: 'destructive',
        style: { direction: 'rtl' },
      });
    } finally {
      setIsRenaming(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    if (editEvent) {
      setStep(2);
      setSelectedProject(editEvent.projectId);
      setEventType(editEvent.type);
      setEventDate(new Date(editEvent.date).toISOString().split('T')[0]);
      setDescription(editEvent.text || '');
      setSelectedTags((editEvent.tags || []).map(t => t.id));
      setChangeReason(''); // every edit session starts with an empty reason

      const files = editEvent.files || getEventFiles(editEvent.id) || [];
      setExistingAttachments(files);
      setRemovedAttachmentIds([]);
      setPendingAttachments([]);
    }
    setAvailableTags(userDetails?.Tags || [])
    setNewTagName('');
    setShowNewTagInput(false);
    setIsCreatingTag(false);
  }, [open, editEvent, getEventFiles]);

  const resetForm = () => {
    setStep(preselectedProjectId || isEditMode ? 2 : 1);
    setSelectedProject(preselectedProjectId || '');
    setEventType('visit');
    setEventDate(new Date().toISOString().split('T')[0]);
    setDescription('');
    setSelectedTags([]);
    setChangeReason('');
    setPendingAttachments([]);
    setPreviewIndex(null);
    setEditIndex(null);
    setExistingAttachments([]);
    setRemovedAttachmentIds([]);
    setIsSaving(false);
    setAvailableTags(userDetails?.Tags || [])
    setNewTagName('');
    setShowNewTagInput(false);
    setIsCreatingTag(false);
    setSelectionMode(false);
    setSelectedKeys(new Set());
    setIsSharing(false);
    // Draft state — clear so the next fresh "create" starts with no draft.
    setDraftEventId(null);
    draftEventIdRef.current = null;
    draftPromiseRef.current = null;
    lastSavedRef.current = {};
    uploadPromisesRef.current = new Map();
  };

  const handleClose = (success: boolean) => {
    resetForm();
    onClose(success);
  };

  const makeUid = () => `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

  const patchAttachmentByUid = (uid: string, patch: Partial<PendingAttachment>) => {
    setPendingAttachments(prev => prev.map(a => (a.uid === uid ? { ...a, ...patch } : a)));
  };

  const ensureDraftExists = async (): Promise<string | null> => {
    if (isEditMode || !selectedProject) return null;
    if (draftEventIdRef.current) return draftEventIdRef.current;
    if (draftPromiseRef.current) return draftPromiseRef.current;
    const p = (async () => {
      const draft = await EventService.createDraftEvent({ projectId: selectedProject });
      draftEventIdRef.current = draft.id;
      setDraftEventId(draft.id);
      return draft.id as string;
    })();
    draftPromiseRef.current = p;
    try {
      return await p;
    } catch {
      draftPromiseRef.current = null; // allow retry on the next action
      return null;
    }
  };

  const saveDraftField = async (patch: UpdateEventPayload) => {
    if (isEditMode) return;
    const changed = (Object.keys(patch) as (keyof UpdateEventPayload)[]).some(
      key => JSON.stringify(patch[key]) !== JSON.stringify(lastSavedRef.current[key])
    );
    if(!patch.type) patch.type = eventType;
    if(!patch.date) patch.date = eventDate;
    if (!changed) return;
    const id = await ensureDraftExists();
    if (!id) return;
    try {
      await EventService.updateDraftEvent(id, patch);
      reloadEvents?.();
      Object.assign(lastSavedRef.current, patch);
    } catch {
      // silent; flushed again at publish
    }
  };

  const buildFileFormData = (att: PendingAttachment, eventId: string): FormData => {
    const isImage = att.file.type.startsWith('image/');
    const isVideo = att.file.type.startsWith('video/');
    const category: FileCategory = isImage || isVideo ? 'photos' : 'contracts';

    let fileBlob: Blob | null = null;
    if (att.file.type.includes('image')) {
      fileBlob = fileService.base64ToBlob(att.base64 || '', att.file.type);
    }

    const formData = new FormData();
    formData.append('file', fileBlob ? fileBlob : att.file, att.file.name);
    formData.append('projectId', selectedProject);
    formData.append('eventId', eventId);
    formData.append('category', category);
    if (isVideo) formData.append('thumbnail', att.preview || '');
    formData.append('isCameraCapture', att.isCameraCapture ? 'true' : 'false');
    formData.append('notes', att.notes ?? '');
    return formData;
  };

  // Create-mode only: upload one attachment against the draft immediately (or replace its backend copy
  // when the binary was edited). Upload-new-then-delete-old keeps at least one copy if the delete fails.
  // Concurrent calls for the same attachment share one promise so a file is never uploaded twice.
  const syncAttachmentToDraft = (att: PendingAttachment): Promise<void> => {
    const uid = att.uid;
    if (!uid) return Promise.resolve();
    const existing = uploadPromisesRef.current.get(uid);
    if (existing) return existing;

    const p = (async () => {
      const id = await ensureDraftExists();
      if (!id) return;
      const prevFileId = att.uploadedFileId;
      const notesSent = att.notes ?? '';
      try {
        const res = await fileService.UploadFile(buildFileFormData(att, id));
        const newFileId = (res?.id as string | undefined);
        patchAttachmentByUid(uid, {
          uploaded: true,
          binaryDirty: false,
          uploadedFileId: newFileId ?? prevFileId,
          uploadedNotes: notesSent,
        });
        if (prevFileId && newFileId && prevFileId !== newFileId) {
          await fileService.deleteFile(prevFileId).catch(() => {}); // drop the stale copy
        }
      } catch {
        // silent; reconciled at publish
      }
    })();

    uploadPromisesRef.current.set(uid, p);
    void p.finally(() => uploadPromisesRef.current.delete(uid));
    return p;
  };

  // Map backend publish 400 (`missingFields`) onto the existing Hebrew validation UX.
  const showPublishValidationError = (missingFields: string[]) => {
    const fields = missingFields || [];
    if (fields.includes('text')) {
      toast({ title: 'יש להזין תיאור', description: 'לא ניתן לשמור תיעוד ללא תיאור', variant: 'destructive' });
    } else if (fields.includes('date') || fields.includes('type')) {
      toast({ title: 'חסרים פרטים', description: 'יש למלא תאריך וסוג תיעוד', variant: 'destructive' });
    } else {
      toast({ title: 'לא ניתן לשמור את התיעוד', description: 'חסרים שדות חובה', variant: 'destructive' });
    }
  };

  const convertFileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleFilesSelected = async (files: FileList | null, isCameraCapture = false) => {

    if (!files || files.length === 0) return;

    const newAttachments: PendingAttachment[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const isImage = file.type.startsWith('image/');
      const isVideo = file.type.startsWith('video/');

      if ((isImage || isVideo) && file.size > 200 * 1024 * 1024) {
        toast({
          title: 'קובץ גדול מדי',
          description: `${file.name} גדול מ-200MB ולא יכול להישמר`,
          variant: 'destructive'
        });
        continue;
      }

      let base64: string | null = null;
      let preview: string | null = null;

      if (isImage) {
        base64 = await convertFileToBase64(file);
        preview = base64;
      } else {
        // For non-images, we'll generate a mock URL but keep the file info
        base64 = '#mock-url';
        preview = null;
      }

      if(isVideo){
        preview = await generateVideoThumbnail(file);
      }

      newAttachments.push({ file, preview, base64, isCameraCapture, uid: makeUid() });
    }

    setPendingAttachments(prev => [...prev, ...newAttachments]);

    // Create mode: upload each new attachment immediately against the draft event.
    if (!isEditMode) {
      newAttachments.forEach(att => { void syncAttachmentToDraft(att); });
    }
  };

  const removeAttachment = (index: number) => {
    // Create mode: if this attachment was already uploaded to the draft, delete its backend copy too.
    const att = pendingAttachments[index];
    if (!isEditMode && att?.uploaded && att.uploadedFileId) {
      void fileService.deleteFile(att.uploadedFileId).catch(() => {});
    }
    setPendingAttachments(prev => prev.filter((_, i) => i !== index));
    // Close any open preview/editor so they never point at a stale/shifted index.
    setPreviewIndex(null);
    setEditIndex(null);
    // Removing shifts pending indices, so `p:{index}` selection keys would be stale — clear them.
    exitSelection();
  };

  const removeExistingAttachment = (fileId: string) => {
    setRemovedAttachmentIds(prev => (prev.includes(fileId) ? prev : [...prev, fileId]));
    setSelectedKeys(prev => {
      if (!prev.has(existingKey(fileId))) return prev;
      const next = new Set(prev);
      next.delete(existingKey(fileId));
      if (next.size === 0) setSelectionMode(false);
      return next;
    });
  };

  const handleEditSubmit = async () => {
    if (!editEvent) return;
    setIsSaving(true);
    try {
      try {
        await EventService.updateEvent(editEvent.id, {
          date: eventDate,
          text: description,
          type: eventType,
          tagIds: selectedTags,
          changeReason, // sent untrimmed — the backend normalizes
        });
      } catch (error) {
        // Session lifecycle (401 → refresh/logout) is owned by the global Axios interceptor.
        throw error;
      }

      let deleteFailures = 0;
      for (const fileId of removedAttachmentIds) {
        try {
          await fileService.deleteFile(fileId);
        } catch (error) {
          deleteFailures++;
        }
      }
      if (deleteFailures > 0) {
        toast({
          title: 'חלק מהקבצים לא נמחקו',
          description: `${deleteFailures} קבצים נכשלו במחיקה`,
          variant: 'destructive',
        });
      }

      for (const attachment of pendingAttachments) {
        const isImage = attachment.file.type.startsWith('image/');
        const isVideo = attachment.file.type.startsWith('video/');
        const category: FileCategory = isImage || isVideo ? 'photos' : 'contracts';

        let fileBlob: Blob | null = null;
        if (attachment.file.type.includes('image')) {
          fileBlob = fileService.base64ToBlob(attachment.base64 || '', attachment.file.type);
        }

        const formData = new FormData();
        formData.append('file', fileBlob ? fileBlob : attachment.file, attachment.file.name);
        formData.append('projectId', editEvent.projectId);
        formData.append('eventId', editEvent.id);
        formData.append('category', category);
        if (isVideo) {
          formData.append('thumbnail', attachment.preview || '');
        }
        formData.append('isCameraCapture', attachment.isCameraCapture ? 'true' : 'false');
        formData.append('notes', attachment.notes ?? '');
        try {
          await fileService.UploadFile(formData);
        } catch (error) {
          // continue with other uploads
        }
      }

      toast({
        title: 'התיעוד עודכן בהצלחה',
      });
      handleClose(true);
    } catch (error) {
      toast({
        title: 'שגיאה בעדכון',
        description: 'אירעה שגיאה בעדכון התיעוד',
        variant: 'destructive',
      });
      setIsSaving(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedProject || !description.trim()) return;

    if (isEditMode) {
      return handleEditSubmit();
    }

    setIsSaving(true);

    try {
      // Ensure the draft exists (covers the case where no field ever triggered its creation).
      const id = await ensureDraftExists();
      if (!id) {
        toast({ title: 'שגיאה בשמירה', description: 'אירעה שגיאה ביצירת התיעוד', variant: 'destructive' });
        setIsSaving(false);
        return;
      }

      // Flush every field so a typed-but-not-blurred value (e.g. description) is never lost.
      try {
        await EventService.updateDraftEvent(id, {
          date: eventDate,
          type: eventType,
          text: description,
          tagIds: selectedTags,
        });
        lastSavedRef.current = { date: eventDate, type: eventType, text: description, tagIds: selectedTags };
      } catch (error) {
        // Session lifecycle (401 → refresh/logout) is owned by the global Axios interceptor.
        // otherwise continue — publish will validate the draft
      }

      // Reconcile binaries so the backend matches local exactly: upload anything not yet sent and
      // replace any image whose binary was edited after its first upload. Notes-only changes are NOT
      // re-uploaded here (handled honestly below).
      const notesUnsynced = pendingAttachments.some(
        a => a.uploaded && !a.binaryDirty && (a.notes ?? '') !== (a.uploadedNotes ?? '')
      );
      for (const att of pendingAttachments) {
        if (!att.uploaded || att.binaryDirty) {
          await syncAttachmentToDraft(att);
        }
      }

      // Publish the draft.
      try {
        await EventService.publishDraftEvent(id);
      } catch (error) {
        // Session lifecycle (401 → refresh/logout) is owned by the global Axios interceptor.
        if (error.response && error.response.status === 400) {
          showPublishValidationError(error.response.data?.missingFields || []);
          setIsSaving(false);
          return;
        }
        throw error;
      }

      // Honest notes-sync limitation: notes edited after a file was already uploaded can't be pushed
      // to the backend (no file-update endpoint) — surface it instead of pretending they were saved.
      if (notesUnsynced) {
        console.warn('[NewDocumentModal] File notes edited after upload were not synced to the backend (no PATCH /files endpoint).');
        toast({
          title: 'הערות לחלק מהקבצים נשמרו מקומית בלבד',
          description: 'לא ניתן לעדכן הערות לקובץ שכבר הועלה',
        });
      }

      toast({ title: 'התיעוד נשמר בהצלחה' });
      // Single source of truth: parent onClose(true) → existing reloadEvents() refreshes the timeline.
      handleClose(true);
    } catch (error) {
      toast({
        title: 'שגיאה בשמירה',
        description: 'אירעה שגיאה בשמירת התיעוד',
        variant: 'destructive'
      });
      setIsSaving(false);
    }
  };

  const toggleTag = (tagId: string) => {
    const next = selectedTags.includes(tagId)
      ? selectedTags.filter(id => id !== tagId)
      : [...selectedTags, tagId];
    setSelectedTags(next);
    saveDraftField({ tagIds: next }); // create-mode only (no-op in edit)
  };

  const handleCreateTag = async () => {
    const name = newTagName.trim();
    if (!name) return;

    // Gracefully handle duplicates without hitting the API
    const existing = availableTags.find(
      t => t.tagName.trim().toLowerCase() === name.toLowerCase()
    );
    if (existing) {
      const next = selectedTags.includes(existing.id) ? selectedTags : [...selectedTags, existing.id];
      setSelectedTags(next);
      saveDraftField({ tagIds: next }); // create-mode only (no-op in edit)
      setNewTagName('');
      setShowNewTagInput(false);
      toast({
        title: 'התגית כבר קיימת',
        description: 'התגית נבחרה עבור התיעוד',
      });
      return;
    }

    setIsCreatingTag(true);
    try {
      const created: UserTag = await userService.addUserTags(name);
      // Make it visible immediately in this modal, globally, and select it
      setAvailableTags(prev => [...prev, created]);
      if (userDetails) {
        setUserDetails({ ...userDetails, Tags: [...(userDetails.Tags ?? []), created] });
      }
      const next = [...selectedTags, created.id];
      setSelectedTags(next);
      saveDraftField({ tagIds: next }); // create-mode only (no-op in edit)
      setNewTagName('');
      setShowNewTagInput(false);
    } catch (error) {
      toast({
        title: 'שגיאה בהוספת תגית',
        description: 'אירעה שגיאה ביצירת התגית, נסה שוב',
        variant: 'destructive',
      });
    } finally {
      setIsCreatingTag(false);
    }
  };

  const handlePhotoButtonClick = () => {
    if (window.innerWidth < 768) {
      handleCameraClick();
    } else {
      galleryInputRef.current?.click();
    }
  };

  const handleCameraClick = () => {
  if (Capacitor.isNativePlatform()) {
    setShowCameraPreview(true);
  } else {
    cameraInputRef.current?.click();
  }
};

  if (!open) return null;

  // Renders a single pending-attachment tile. `index` is the position in the full
  // `pendingAttachments` array so preview/remove/selection keys stay correct even when the
  // tiles are split across the two source sections below.
  const renderPendingTile = (attachment: PendingAttachment, index: number) => {
    const canPreview =
      attachment.file.type.startsWith('image/') || attachment.file.type.startsWith('video/');
    const key = pendingKey(index);
    const isSelected = selectedKeys.has(key);
    return (
      <PendingAttachmentTile
        key={index}
        attachment={attachment}
        isAndroid={isAndroid}
        selectionMode={selectionMode}
        isSelected={isSelected}
        canPreview={canPreview}
        tileProps={tileHandlers(key, canPreview ? () => setPreviewIndex(index) : undefined)}
        onRemove={() => removeAttachment(index)}
        onToggleSelect={() => toggleSelection(key)}
      />
    );
  };

  // Split pending attachments by source while preserving original indices.
  const cameraPendingAttachments = pendingAttachments
    .map((attachment, index) => ({ attachment, index }))
    .filter(({ attachment }) => attachment.isCameraCapture === true);
  const regularPendingAttachments = pendingAttachments
    .map((attachment, index) => ({ attachment, index }))
    .filter(({ attachment }) => attachment.isCameraCapture !== true);

  // Split existing attachments by camera source as well.
  const cameraExistingAttachments = existingAttachments
    .filter(f => !removedAttachmentIds.includes(f.id))
    .filter(f => f.isCameraCapture === true);
  const regularExistingAttachments = existingAttachments
    .filter(f => !removedAttachmentIds.includes(f.id))
    .filter(f => f.isCameraCapture !== true);

  return (
    <>
    <div className="fixed inset-0 bg-foreground/50 z-50 flex items-end md:items-center justify-center">
      <div className="bg-card w-full md:w-[500px] md:max-h-[90vh] rounded-t-2xl md:rounded-2xl overflow-hidden animate-slide-up shadow-soft">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h2 className="text-lg font-bold">{isEditMode ? 'עריכת תיעוד' : 'תיעוד חדש'}</h2>
          <button
            onClick={() => handleClose(false)}
            disabled={isSaving}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-secondary transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5 max-h-[70vh] overflow-y-auto">
          {step === 1 && (
            <div className="space-y-4">
              <p className="text-muted-foreground mb-4">בחר פרויקט:</p>
              <div className="space-y-2">
                {projects.filter(p => p.status === 'active').map((project) => (
                  <button
                    key={project.id}
                    onClick={() => {
                      setSelectedProject(project.id);
                      setStep(2);
                    }}
                    className="w-full text-right p-4 rounded-xl border border-border hover:border-primary hover:bg-accent transition-all"
                  >
                    <p className="font-medium">{project.name}</p>
                    <p className="text-sm text-muted-foreground">{project.clientName}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium mb-3">תאריך</label>
                <input
                  type="date"
                  value={eventDate}
                  onChange={(e) => {
                    setEventDate(e.target.value);
                    saveDraftField({ date: e.target.value }); // create-mode only (no-op in edit)
                  }}
                  className="w-full px-4 py-3 rounded-xl border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>
              {/* Event Type */}
              <div>
                <label className="block text-sm font-medium mb-3">סוג תיעוד</label>
                <div className="flex gap-2">
                  {eventTypes.map((type) => (
                    <button
                      key={type.value}
                      onClick={() => {
                        setEventType(type.value);
                        saveDraftField({ type: type.value }); // create-mode only (no-op in edit)
                      }}
                      className={cn(
                        "flex-1 py-3 px-4 rounded-xl text-sm font-medium transition-all",
                        eventType === type.value
                          ? `${type.color} text-primary-foreground`
                          : "bg-secondary text-secondary-foreground hover:bg-secondary/80"
                      )}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Tags */}
              <TagSelector
                availableTags={availableTags}
                selectedTags={selectedTags}
                toggleTag={toggleTag}
                showNewTagInput={showNewTagInput}
                setShowNewTagInput={setShowNewTagInput}
                newTagName={newTagName}
                setNewTagName={setNewTagName}
                handleCreateTag={handleCreateTag}
                isCreatingTag={isCreatingTag}
              />

              {/* Description */}
              <div>
                <label className="block text-sm font-medium mb-3">תיאור</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  onBlur={() => saveDraftField({ text: description })} // create-mode only (no-op in edit)
                  placeholder="תאר את התיעוד..."
                  rows={4}
                  className="w-full px-4 py-3 rounded-xl border border-input bg-background resize-none focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>

              {/* Photo Upload */}
              <div>
                <label className="block text-sm font-medium mb-3">תמונות</label>
                <input
                  ref={cameraInputRef}
                  type="file"
                  multiple
                  accept="image/*,video/*"
                  capture="environment"
                  className="hidden"
                  onChange={(e) => handleFilesSelected(e.target.files, true)}
                />
                <input
                  ref={galleryInputRef}
                  type="file"
                  multiple
                  accept="image/*,video/*"
                  className="hidden"
                  onChange={(e) => handleFilesSelected(e.target.files)}
                />
                <button
                  onClick={handlePhotoButtonClick}
                  className="w-full p-8 border-2 border-dashed border-border rounded-xl flex flex-col items-center gap-2 text-muted-foreground hover:border-primary hover:text-primary transition-colors"
                >
                  <Camera className="w-8 h-8" />
                  <span className="text-sm">הוסף תמונה</span>
                </button>
              </div>

              {/* File Attachments Section */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium mb-3">
                  <Paperclip className="w-4 h-4" />
                  קבצים מצורפים
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={(e) => handleFilesSelected(e.target.files)}
                />
                <Button
                  variant="outline"
                  className="w-full mb-4"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload className="w-4 h-4 ml-2" />
                  הוסף קבצים
                </Button>

                {/* Selection bar — Android multi-select mode, or any web selection. */}
                {((isAndroid && selectionMode) || (!isAndroid && selectedKeys.size > 0)) && (
                  <div className="flex items-center justify-between gap-2 mb-3 px-3 py-2 rounded-xl bg-primary/10 border border-primary/30">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={exitSelection}
                        className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-secondary transition-colors"
                        aria-label="בטל בחירה"
                      >
                        <X className="w-4 h-4" />
                      </button>
                      <span className="text-sm font-medium">{selectedKeys.size} נבחרו</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {selectedKeys.size === 1 && (
                        <button
                          type="button"
                          onClick={() => setRenameTarget([...selectedKeys][0])}
                          disabled={isSharing}
                          className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-secondary text-secondary-foreground text-sm font-medium disabled:opacity-50 hover:opacity-90 transition-opacity"
                        >
                          <Pencil className="w-4 h-4" />
                          שנה שם
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={handleShare}
                        disabled={isSharing || selectedKeys.size === 0}
                        className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium disabled:opacity-50 hover:opacity-90 transition-opacity"
                      >
                        {isSharing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
                        שתף
                      </button>
                    </div>
                  </div>
                )}

                {/* Existing Attachments (edit mode) */}
                {isEditMode && (cameraExistingAttachments.length > 0 || regularExistingAttachments.length > 0) && (
                  <>
                    {cameraExistingAttachments.length > 0 && (
                      <div className="mb-4">
                        <p className="text-sm font-medium mb-2">תמונות וסרטונים</p>
                        <div className="grid grid-cols-3 gap-2">
                          {cameraExistingAttachments.map((file) => {
                            const key = existingKey(file.id);
                            const isSelected = selectedKeys.has(key);
                            return (
                              <ExistingAttachmentTile
                                key={file.id}
                                file={file}
                                isAndroid={isAndroid}
                                selectionMode={selectionMode}
                                isSelected={isSelected}
                                tileProps={tileHandlers(key)}
                                onRemove={() => removeExistingAttachment(file.id)}
                                onToggleSelect={() => toggleSelection(key)}
                              />
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {regularExistingAttachments.length > 0 && (
                      <div className="mb-4">
                        <p className="text-sm font-medium mb-2">קבצים מצורפים</p>
                        <div className="grid grid-cols-3 gap-2">
                          {regularExistingAttachments.map((file) => {
                            const key = existingKey(file.id);
                            const isSelected = selectedKeys.has(key);
                            return (
                              <ExistingAttachmentTile
                                key={file.id}
                                file={file}
                                isAndroid={isAndroid}
                                selectionMode={selectionMode}
                                isSelected={isSelected}
                                tileProps={tileHandlers(key)}
                                onRemove={() => removeExistingAttachment(file.id)}
                                onToggleSelect={() => toggleSelection(key)}
                              />
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* Pending Attachments Preview — split by source */}
                {cameraPendingAttachments.length > 0 && (
                  <div className="mb-4">
                    <p className="text-sm font-medium mb-2">תמונות וסרטונים</p>
                    <div className="grid grid-cols-3 gap-2">
                      {cameraPendingAttachments.map(({ attachment, index }) =>
                        renderPendingTile(attachment, index)
                      )}
                    </div>
                  </div>
                )}

                {regularPendingAttachments.length > 0 && (
                  <div>
                    <p className="text-sm font-medium mb-2">קבצים מצורפים</p>
                    <div className="grid grid-cols-3 gap-2">
                      {regularPendingAttachments.map(({ attachment, index }) =>
                        renderPendingTile(attachment, index)
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Change reason — audit metadata for the current edit save only (edit mode only) */}
              {isEditMode && (
                <div className="border-t border-border pt-4">
                  <label className="block text-sm font-medium mb-1">
                    מהות השינוי <span className="text-xs text-muted-foreground font-normal">(אופציונלי)</span>
                  </label>
                  <p className="text-xs text-muted-foreground mb-3">הסבר קצר מדוע בוצע השינוי (אופציונלי)</p>
                  <textarea
                    value={changeReason}
                    onChange={(e) => setChangeReason(e.target.value)}
                    placeholder="לדוגמה: לאחר בדיקה התברר שמדובר בשאלה ולא בתקלה"
                    rows={3}
                    maxLength={500}
                    className="w-full px-4 py-3 rounded-xl border border-input bg-background resize-none focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-5 border-t border-border">
          {step === 2 && !preselectedProjectId && !isEditMode && (
            <button
              onClick={() => setStep(1)}
              disabled={isSaving}
              className="px-6 py-3 rounded-xl bg-secondary text-secondary-foreground font-medium hover:bg-secondary/80 transition-colors disabled:opacity-50"
            >
              חזור
            </button>
          )}
          <button
            onClick={step === 2 ? handleSubmit : undefined}
            disabled={(step === 2 && !description.trim()) || isSaving}
            className="flex-1 px-6 py-3 rounded-xl gradient-primary text-primary-foreground font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
          >
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {isEditMode ? 'שומר שינויים...' : 'שומר אירוע וקבצים...'}
              </>
            ) : (
              step === 2 ? (isEditMode ? 'שמור שינויים' : 'שמור תיעוד') : 'המשך'
            )}
          </button>
        </div>
      </div>
    </div>
    {showCameraPreview && (
      <CameraPreviewModal
        open={showCameraPreview}
        onClose={() => setShowCameraPreview(false)}
        onCapture={(file) => {
          const dataTransfer = new DataTransfer();
          dataTransfer.items.add(file);
          handleFilesSelected(dataTransfer.files, true);
          setShowCameraPreview(false);
        }}
      />)}

    {/* Attachment preview (image viewer / video player) */}
    {previewIndex !== null && pendingAttachments[previewIndex] && (
      <AttachmentPreviewModal
        key={previewIndex}
        attachment={pendingAttachments[previewIndex]}
        open={previewIndex !== null}
        onClose={() => setPreviewIndex(null)}
        onEdit={() => {
          setEditIndex(previewIndex);
          setPreviewIndex(null);
        }}
        onNotesUpdated={(notes) =>
          setPendingAttachments(prev =>
            prev.map((a, i) => (i === previewIndex ? { ...a, notes } : a))
          )
        }
      />
    )}

    {/* Image editor (draw / text annotations) */}
    {editIndex !== null && pendingAttachments[editIndex] && (
      <ImageEditor
        src={pendingAttachments[editIndex].preview || ''}
        fileName={pendingAttachments[editIndex].file.name}
        mimeType={pendingAttachments[editIndex].file.type}
        onCancel={() => setEditIndex(null)}
        onSave={(file, dataUrl) => {
          // The binary changed → mark it for re-upload/replace at publish (create mode).
          setPendingAttachments(prev =>
            prev.map((a, i) => (i === editIndex ? { ...a, file, preview: dataUrl, base64: dataUrl, binaryDirty: true } : a))
          );
          setEditIndex(null);
        }}
      />
    )}

    <RenameFileDialog
      open={renameTarget !== null}
      currentName={getSelectedFileName(renameTarget)}
      isSaving={isRenaming}
      onCancel={() => setRenameTarget(null)}
      onSave={handleRename}
    />
    </>
  );
}
